import React, { useState, useEffect } from "react";
import { useMotionTemplate, useMotionValue, motion } from "motion/react";

const cn = (...classes) => classes.filter(Boolean).join(" ");

const Input = React.forwardRef(({ className, type, ...props }, ref) => {
  const radius = 100;
  const [visible, setVisible] = React.useState(false);

  let mouseX = useMotionValue(0);
  let mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }) {
    let { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }
  return (
    <motion.div
      style={{
        background: useMotionTemplate`
          radial-gradient(
            ${visible ? radius + "px" : "0px"} circle at ${mouseX}px ${mouseY}px,
            #3b82f6,
            transparent 80%
          )
        `,
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      className="group/input rounded-lg p-[2px] transition duration-300"
    >
      <input
        type={type}
        className={cn(
          `flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-black shadow-sm transition duration-400 placeholder:text-neutral-400 focus-visible:ring-2 focus-visible:ring-[#A14D1F] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50`,
          className,
        )}
        ref={ref}
        {...props}
      />
    </motion.div>
  );
});
Input.displayName = "Input";

// AnimatedSelect component
const AnimatedSelect = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    const radius = 100;
    const [visible, setVisible] = React.useState(false);
    let mouseX = useMotionValue(0);
    let mouseY = useMotionValue(0);

    function handleMouseMove({ currentTarget, clientX, clientY }) {
      let { left, top } = currentTarget.getBoundingClientRect();
      mouseX.set(clientX - left);
      mouseY.set(clientY - top);
    }

    return (
      <motion.div
        style={{
          background: useMotionTemplate`
          radial-gradient(
            ${visible ? radius + "px" : "0px"} circle at ${mouseX}px ${mouseY}px,
            #3b82f6,
            transparent 60%
          )
        `,
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        className="group/input rounded-lg p-[2px] transition duration-300"
      >
        <select
          ref={ref}
          className={cn(
            "flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-black shadow-sm transition duration-400 placeholder:text-neutral-400 focus-visible:ring-2 focus-visible:ring-[#A14D1F] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
          {...props}
        >
          {children}
        </select>
      </motion.div>
    );
  },
);
AnimatedSelect.displayName = "AnimatedSelect";

// AnimatedTextarea component
const AnimatedTextarea = React.forwardRef(({ className, ...props }, ref) => {
  const radius = 100;
  const [visible, setVisible] = React.useState(false);
  let mouseX = useMotionValue(0);
  let mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }) {
    let { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <motion.div
      style={{
        background: useMotionTemplate`
          radial-gradient(
            ${visible ? radius + "px" : "0px"} circle at ${mouseX}px ${mouseY}px,
            #3b82f6,
            transparent 80%
          )
        `,
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      className="group/input rounded-lg p-[2px] transition duration-300"
    >
      <textarea
        ref={ref}
        className={cn(
          "flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-black shadow-sm transition duration-400 placeholder:text-neutral-400 focus-visible:ring-2 focus-visible:ring-[#A14D1F] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      />
    </motion.div>
  );
});
AnimatedTextarea.displayName = "AnimatedTextarea";

const initialState = {
  CandidateName: "",
  Address: "",
  Email: "",
  Phone: "",
  FathersPhone: "",
  Qualification: "",
  Experience: "",
  Course: "",
  AOI: "",
  Message: "",
};

const courses = [
  "Acting",
  "Photography",
  "Videography",
  "Editing",
  "Direction",
  "Production",
  "Script Writing",
  "Sound Design",
  "Music Production",
  "Dance",
  "Fashion Design",
  "Makeup Artistry",
  "Graphic Design",
  "Digital Marketing",
];

const areasOfInterest = [
  "Complete Film Production",
  "Studios and Equipment for Event Shoot",
  "Pre & Post Wedding Shoot Venue",
  "Biography and Documentary Shoot Management",
  "Training and Podcast Shoot Management",
  "Sound Recording Studio",
  "Industrial Tour",
  "Podcast Studio",
  "Chroma Cinematic Studio",
  "Black Box Photography Studio",
  "Corporate Shoot",
  "Event Coverage",
  "Equipment Rental",
];

const AdmissionMain = () => {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [aoiOptions, setAoiOptions] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch AOI options on component mount
  useEffect(() => {
    // Use static data with IDs as fallback (endpoint doesn't exist)
    const staticAoiOptions = areasOfInterest.map((name, index) => ({
      id: index + 1,
      name: name,
    }));
    console.log("Setting AOI options from static data:", staticAoiOptions);
    setAoiOptions(staticAoiOptions);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Only allow digits for phone fields
    if ((name === "Phone" || name === "FathersPhone") && value) {
      const numbersOnly = value.replace(/[^0-9]/g, "").slice(0, 10);
      setForm({ ...form, [name]: numbersOnly });
      return;
    }

    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!form.CandidateName.trim()) {
      setSnackbar({
        open: true,
        message: "Candidate Name is required.",
        severity: "error",
      });
      return;
    }
    if (!form.Email.trim()) {
      setSnackbar({
        open: true,
        message: "Email is required.",
        severity: "error",
      });
      return;
    }
    if (!form.Phone.trim() || form.Phone.length !== 10) {
      setSnackbar({
        open: true,
        message: "Phone must be 10 digits.",
        severity: "error",
      });
      return;
    }
    if (!form.FathersPhone.trim() || form.FathersPhone.length !== 10) {
      setSnackbar({
        open: true,
        message: "Father's Phone must be 10 digits.",
        severity: "error",
      });
      return;
    }
    if (!form.Address.trim()) {
      setSnackbar({
        open: true,
        message: "Address is required.",
        severity: "error",
      });
      return;
    }
    if (!form.Qualification.trim()) {
      setSnackbar({
        open: true,
        message: "Qualification is required.",
        severity: "error",
      });
      return;
    }
    if (!form.Course) {
      setSnackbar({
        open: true,
        message: "Course is required.",
        severity: "error",
      });
      return;
    }
    if (!form.AOI) {
      setSnackbar({
        open: true,
        message: "Area of Interest is required.",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admission-forms/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        },
      );
      if (res.ok) {
        setSnackbar({
          open: true,
          message: "Form submitted successfully!",
          severity: "success",
        });
        setForm(initialState);
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error(
          "Full API Error Response:",
          JSON.stringify(errorData, null, 2),
        );
        console.error("Form Data Sent:", form);

        // Format error message from API
        let errorMessage = "Submission failed. Please try again.";
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.AOI) {
          // Show AOI specific error
          errorMessage = Array.isArray(errorData.AOI)
            ? `AOI Error: ${errorData.AOI[0]}`
            : `AOI Error: ${errorData.AOI}`;
          console.error("AOI Error Details:", errorData.AOI);
        } else {
          // Extract first error from field errors
          const firstFieldError = Object.entries(errorData)[0];
          if (firstFieldError) {
            const [field, error] = firstFieldError;
            const errorMsg = Array.isArray(error) ? error[0] : error;
            errorMessage = `${field}: ${errorMsg}`;
          }
        }

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Network Error:", error);
      setSnackbar({
        open: true,
        message: "Network error. Please try again.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-white">
      <div className="relative z-10 w-full p-10 bg-white border-gray-200 rounded-none max-w-7xl mb-9">
        <div className="flex flex-col items-center mb-8">
          <h2 className="text-4xl font-extrabold tracking-tight text-center text-[#A14D1F] drop-shadow-lg">
            Admission Form
          </h2>
          <p className="mt-2 text-lg font-medium text-center text-[#4A2F24]">
            Apply for your dream course today!
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label className="font-semibold text-[#A14D1F]">
                Candidate Name
              </label>
              <Input
                placeholder="Enter your name"
                name="CandidateName"
                className="bg-white"
                value={form.CandidateName}
                onChange={handleChange}
                onKeyPress={(e) => {
                  if (/[&^%*@!#$()<>?/-]/.test(e.key)) e.preventDefault();
                }}
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-semibold text-[#A14D1F]">Email</label>
              <Input
                placeholder="Enter email"
                name="Email"
                className="bg-white"
                value={form.Email}
                onChange={handleChange}
                type="email"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label className="font-semibold text-[#A14D1F]">Phone</label>
              <Input
                placeholder="Enter phone number"
                name="Phone"
                value={form.Phone}
                className="bg-white"
                onChange={handleChange}
                type="tel"
                required
                maxLength={10}
                minLength={10}
              />
              <small className="text-gray-500">10 digit phone number</small>
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-semibold text-[#A14D1F]">
                Father's Phone
              </label>
              <Input
                placeholder="Enter father's phone"
                name="FathersPhone"
                className="bg-white"
                value={form.FathersPhone}
                onChange={handleChange}
                type="tel"
                required
                maxLength={10}
                minLength={10}
              />
              <small className="text-gray-500">10 digit phone number</small>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label className="font-semibold text-[#A14D1F]">
                Qualification
              </label>
              <Input
                placeholder="Enter qualification"
                name="Qualification"
                className="bg-white"
                value={form.Qualification}
                onChange={handleChange}
                onKeyPress={(e) => {
                  if (/[&^%*@!#$()<>?/-]/.test(e.key)) e.preventDefault();
                }}
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-semibold text-[#A14D1F]">Course</label>
              <AnimatedSelect
                name="Course"
                value={form.Course}
                onChange={handleChange}
                required
              >
                <option value="" disabled hidden>
                  Select Course
                </option>
                {courses.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </AnimatedSelect>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label className="font-semibold text-[#A14D1F]">
                Area of Interest
              </label>
              <AnimatedSelect
                name="AOI"
                value={form.AOI}
                onChange={handleChange}
                required
              >
                <option value="" disabled hidden>
                  Select Area of Interest
                </option>
                {aoiOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name || option.title}
                  </option>
                ))}
              </AnimatedSelect>
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-semibold text-[#A14D1F]">Experience</label>
              <Input
                placeholder="Enter experience"
                name="Experience"
                className="bg-white"
                value={form.Experience}
                onChange={handleChange}
                onKeyPress={(e) => {
                  if (/[&^%*@!#$()<>?/-]/.test(e.key)) e.preventDefault();
                }}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-semibold text-[#A14D1F]">Address</label>
            <AnimatedTextarea
              placeholder="Enter address"
              name="Address"
              value={form.Address}
              onChange={handleChange}
              onKeyPress={(e) => {
                if (/[&^%*@!#$()<>?/-]/.test(e.key)) e.preventDefault();
              }}
              required
              rows={2}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-semibold text-[#A14D1F]">Message</label>
            <AnimatedTextarea
              placeholder="Message (optional)"
              name="Message"
              value={form.Message}
              onChange={handleChange}
              onKeyPress={(e) => {
                if (/[&^%*@!#$()<>?/-]/.test(e.key)) e.preventDefault();
              }}
              rows={6}
            />
          </div>
          <button
            type="submit"
            className="flex items-center justify-center w-full gap-2 py-3 text-lg font-bold text-white transition-all duration-300 shadow-xl mt-7 bg-gradient-to-r from-[#A14D1F] via-[#A14D1F] to-[#4A2F24] rounded-2xl hover:scale-105 hover:shadow-2xl disabled:opacity-60"
          >
            {loading && (
              <svg
                className="w-5 h-5 text-white animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                ></path>
              </svg>
            )}
            {loading ? "Submitting..." : "Submit Application"}
          </button>
          {snackbar.open && (
            <div
              className={`mt-4 text-center py-2 rounded-xl font-semibold text-lg shadow-lg ${snackbar.severity === "success" ? "bg-green-200/80 text-green-900" : "bg-red-200/80 text-red-900"}`}
            >
              {snackbar.message}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AdmissionMain;
